import PatientFormComponent, {
  IAddNewPatientFormValues,
  parsePatientValues,
} from 'src/components/patient/patient-form/patient-form.component'
import { FormProvider, useForm } from 'react-hook-form'
import {
  CurecallButton,
  CurecallModalContainer,
  ECurecallButtonAppearance,
} from '@honestica/curecall-ui'
import { PatientService } from 'src/services/patient.service'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useSession } from 'src/providers/auth.provider'
import { TPatient } from 'src/types/patient'
import { useTranslation } from 'react-i18next'
import { useNotification } from 'src/providers/notification.provider'
import { formatPhoneToInternational } from 'src/utils/phone.util'

type IEditPatientModalComponentProps = {
  isOpen: boolean
  patientId?: number
  onToggle: () => void
  projectId: number
  patient: TPatient
}

const EditPatientModalComponent = ({
  isOpen,
  patientId,
  onToggle,
  projectId,
  patient,
}: IEditPatientModalComponentProps) => {
  const { t } = useTranslation()
  const { notify } = useNotification()
  const { currentUser } = useSession()

  const methods = useForm<IAddNewPatientFormValues>({
    defaultValues: {
      gender: patient.gender ?? undefined,
      firstname: patient.firstname ?? undefined,
      lastname: patient.lastname ?? undefined,
      phone: formatPhoneToInternational(patient.phone),
      birthdate: patient.birthdate ?? undefined,
      hasCaregiver: patient.hasCaregiver,
      MedicalStaffMemberId: patient.MedicalStaffMemberId,
      locale: patient.locale,
      remoteId: patient.remoteId,
    },
  })

  const {
    handleSubmit,
    formState: { errors },
    reset,
  } = methods

  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasCareGiver, setHasCareGiver] = useState(!!patient.hasCaregiver)

  const handleCancel = () => {
    reset()
    onToggle()
  }

  const handleEditPatient = async (values: IAddNewPatientFormValues) => {
    setIsSubmitting(true)
    const newPatient = parsePatientValues(values, patient?.isTestAccount, hasCareGiver)
    try {
      const patientResponse = await PatientService.update(projectId, patientId as number, newPatient)
      queryClient.setQueryData<TPatient>(['patients', projectId, patientId], (oldData) => {
        return oldData ? { ...oldData, ...patientResponse } : patientResponse
      })
      onToggle()
    } catch (e) {
      notify({ title: t('common.actions.error'), message: e, appearance: 'ERROR' })
    }
    setIsSubmitting(false)
  }

  const TStyle = 'c-rounded-full c-pt-0.5 c-mr-2 c-w-6 c-h-6 c-text-center c-bg-muted c-text-white'
  const Title = (
    <span className="c-flex">
      {patient?.isTestAccount && <div className={TStyle}>T</div>}
      {t('components.patient.add-or-edit.edit-patient-modal.modal-title')}
    </span>
  )

  return (
    <CurecallModalContainer
      closeModalAriaLabel={t('components.patient.add-or-edit.edit-patient-modal.curecall-modal-aria-label')}
      title={Title}
      isOpen={isOpen}
      toggle={handleCancel}
      dialogClassName="sm:c-w-[750px] xl:c-w-[1200px]"
    >
      <FormProvider {...methods}>
        <form method="POST" action="#" onSubmit={handleSubmit(handleEditPatient)}>
          <PatientFormComponent
            projectId={projectId}
            currentUser={currentUser}
            setHasCareGiver={setHasCareGiver}
            hasCaregiver={hasCareGiver}
            errors={errors}
            isEditPatient={true}
            patient={patient}
          />
          <div className="c-flex c-justify-center c-items-center c-gap-8 c-mt-10 c-mb-4">
            <CurecallButton appearance={ECurecallButtonAppearance.CANCEL} onClick={handleCancel}>
              {t('components.patient.add-or-edit.edit-patient-modal.cancel-modal-button')}
            </CurecallButton>
            <CurecallButton type="submit" isLoading={isSubmitting}>
              {t('components.patient.add-or-edit.edit-patient-modal.validate-modal-button')}
            </CurecallButton>
          </div>
        </form>
      </FormProvider>
    </CurecallModalContainer>
  )
}

export default EditPatientModalComponent
